import { useState } from 'react';
import { AGREEMENT_DOCS, type AgreementKey } from '../constants/agreements';
import type {
  AgreementModalProps,
  AgreementsStepProps,
  AgreementValues
} from '@/features/auth/types/agreement-step.type.ts';

const ITEMS: {
  key: keyof AgreementValues;
  doc: AgreementKey;
  label: string;
  required: boolean;
}[] = [
  { key: 'agreeTerms', doc: 'terms', label: '이용약관 동의', required: true },
  { key: 'agreePrivacy', doc: 'privacy', label: '개인정보 수집 및 이용 동의', required: true },
  { key: 'agreeMarketing', doc: 'marketing', label: '마케팅 정보 수신 동의', required: false },
  { key: 'agreeEvent', doc: 'event', label: '이벤트 및 혜택 알림 수신 동의', required: false },
];

function CheckIcon({ on }: { on: boolean }) {
  return <span
    className={[
      'grid h-5 w-5 shrink-0 place-items-center rounded-full border transition-colors',
      on ? 'border-brand-500 bg-brand-500 text-white' : 'border-gray-300 text-transparent',
    ].join(' ')}
  >
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
}

/**
 * 약관 동의 스텝.
 * 전체 동의 토글 + 개별 4종(필수 2 / 선택 2). "보기" 클릭 시 전문 모달.
 */
export function AgreementsStep({ values, onChange, errors }: AgreementsStepProps) {
  const [openDoc, setOpenDoc] = useState<{
    key: keyof AgreementValues;
    doc: AgreementKey;
  } | null>(null);

  const allOn = ITEMS.every((it) => values[it.key]);

  const toggleAll = () => {
    const next = !allOn;
    onChange({
      agreeTerms: next,
      agreePrivacy: next,
      agreeMarketing: next,
      agreeEvent: next,
    });
  };

  const toggle = (key: keyof AgreementValues) =>
    onChange({ ...values, [key]: !values[key] });

  return (
    <div className="space-y-3">
      {/* 전체 동의 */}
      <button
        type="button"
        onClick={toggleAll}
        className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3.5 text-left transition-colors hover:border-brand-300"
      >
        <CheckIcon on={allOn} />
        <span className="text-[15px] font-bold text-adobe-dark">
          약관 전체 동의
        </span>
      </button>

      <ul className="space-y-1 px-1">
        {ITEMS.map((it) => (
          <li key={it.key} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => toggle(it.key)}
              className="flex flex-1 items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-black/[0.02]"
            >
              <CheckIcon on={values[it.key]} />
              <span className="flex-1 text-[14px] text-adobe-dark">
                <span
                  className={
                    it.required ? 'font-semibold text-brand-600' : 'text-neutral-950/45'
                  }
                >
                  ({it.required ? '필수' : '선택'})
                </span>{' '}
                {it.label}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setOpenDoc({ key: it.key, doc: it.doc })}
              className="shrink-0 px-2 py-1 text-[12px] text-neutral-950/40 underline transition-colors hover:text-neutral-950"
            >
              보기
            </button>
          </li>
        ))}
      </ul>

      {(errors?.agreeTerms || errors?.agreePrivacy) && (
        <p className="px-1 text-xs text-danger" role="alert">
          {errors?.agreeTerms ?? errors?.agreePrivacy}
        </p>
      )}

      {openDoc && (
        <AgreementModal
          docKey={openDoc.doc}
          onClose={() => setOpenDoc(null)}
          onAgree={() => {
            onChange({ ...values, [openDoc.key]: true });
            setOpenDoc(null);
          }}
        />
      )}
    </div>
  );
}

function AgreementModal({ docKey, onClose, onAgree }: AgreementModalProps) {
  const doc = AGREEMENT_DOCS[docKey];
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-neutral-950/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={doc.title}
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-black/[0.08] px-5 py-4">
          <h3 className="text-[16px] font-bold text-adobe-dark">{doc.title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="grid h-8 w-8 place-items-center rounded-md text-neutral-950/50 transition-colors hover:bg-black/[0.05]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 3L13 13M13 3L3 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-4">
          <p className="whitespace-pre-wrap text-[13px] leading-[1.7] text-neutral-700">
            {doc.content}
          </p>
        </div>

        <footer className="flex gap-2 border-t border-black/[0.08] px-5 py-3.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-adobe-dark transition-colors hover:bg-black/[0.03]"
          >
            닫기
          </button>
          <button
            type="button"
            onClick={onAgree}
            className="flex-1 rounded-lg bg-brand-500 py-2 text-sm font-bold text-white transition-colors hover:bg-brand-600"
          >
            동의하고 닫기
          </button>
        </footer>
      </div>
    </div>
  );
}
